import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './FeaturedHeroCarousel.css';
import spotlightService from '../services/SpotlightService';
import PDPModal from './PDPModal';
import { optimizeImageUrl, getResponsiveImagePreset } from '../utils/ImageUtils';

const FeaturedHeroCarousel = ({ category }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [channels, setChannels] = useState(category?.channels || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [showPDPModal, setShowPDPModal] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const carouselRef = useRef(null);

  // Create an intersection observer to detect when the carousel is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Update visibility state
        setIsVisible(entry.isIntersecting);
      },
      {
        root: null, // viewport
        rootMargin: '100px', // start loading a bit before it comes into view
        threshold: 0.1, // trigger when at least 10% is visible
      },
    );

    // Start observing the carousel element
    if (carouselRef.current) {
      // Store ref value in a variable for cleanup function
      const currentRef = carouselRef.current;
      observer.observe(currentRef);

      // Cleanup observer on unmount using the stored reference
      return () => {
        observer.unobserve(currentRef);
      };
    }

    return undefined;
  }, []);

  // Load channels when the carousel becomes visible
  useEffect(() => {
    const fetchChannels = async () => {
      // Don't fetch if already loaded, loading, or not visible, or no category
      if (!category || channels.length > 0 || loading || !isVisible) {
        return;
      }

      // If category already has channels array with content, use it
      if (Array.isArray(category.channels) && category.channels.length > 0) {
        setChannels(category.channels);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Use SpotlightService to get featured channels by slug

        const response = await spotlightService.getCategoryChannelsBySlug('featured');

        // Extract channels from the response
        let channelsData = [];
        if (response && response.channels && Array.isArray(response.channels)) {
          channelsData = response.channels;
        } else {
          console.error(
            `FeaturedHeroCarousel: Response from SpotlightService is not in expected format:`,
            response,
          );
        }

        setChannels(channelsData);
      } catch (err) {
        console.error(`FeaturedHeroCarousel: Error loading channels:`, err);
        setError(`Failed to load featured content: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchChannels();
  }, [category, channels.length, loading, isVisible]);

  // Slick slider settings
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 8000,
    arrows: true, // Enable arrows for manual navigation
    beforeChange: (current, next) => setCurrentIndex(next),
    customPaging: i => (
      <div
        className={`w-3 h-3 rounded-full ${i === currentIndex ? 'bg-yellow-400' : 'bg-white/50'}`}
      />
    ),
  };

  // Handle watch now button click
  const handleWatchNowClick = () => {
    if (!currentChannel) {return;}

    // If content is locked, navigate to checkout
    if (currentChannel.subscription_access?.unlocked === false) {
      const channelId = currentChannel.dspro_id || currentChannel._id;
      if (channelId) {
        navigate(`/checkout/${channelId}`, { state: { channel: currentChannel } });
      } else {
        console.warn('No channel ID available for checkout navigation');
      }
      return;
    }

    // Otherwise, navigate to watch page
    // Use channel slug for navigation to the watch page
    const channelSlug =
      currentChannel.slug || currentChannel.permalink || currentChannel._id || currentChannel.id;

    // Navigate to the watch page using the /watch/{channelSlug} pattern
    const watchPath = `/watch/${channelSlug}`;

    navigate(watchPath, {
      state: {
        from: location.pathname,
        channel: currentChannel,
      },
    });
  };

  // Handle learn more button click
  const handleLearnMoreClick = channel => {
    setSelectedChannel(channel);
    setShowPDPModal(true);
  };

  // Handle PDP modal close
  const handleClosePDPModal = () => {
    setShowPDPModal(false);
    setSelectedChannel(null);
  };

  // Loading state
  if (loading) {
    return (
      <div
        className="featured-hero-carousel relative w-full h-[500px] mb-8 bg-gray-900"
        ref={carouselRef}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex flex-col items-center animate-pulse">
            <div className="w-full h-64 max-w-3xl mb-4 bg-gray-700 rounded-lg"></div>
            <div className="w-64 h-8 mb-2 bg-gray-700 rounded-lg"></div>
            <div className="w-48 h-4 bg-gray-700 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        className="featured-hero-carousel relative w-full h-[300px] mb-8 bg-gray-900 flex items-center justify-center"
        ref={carouselRef}
      >
        <div className="text-center text-red-400">
          <p>{error}</p>
          <p className="mt-2 text-sm text-white/50">Check browser console for details</p>
        </div>
      </div>
    );
  }

  // No content state
  if (!channels.length) {
    return (
      <div
        className="featured-hero-carousel relative w-full h-[300px] mb-8 bg-gray-900 flex items-center justify-center"
        ref={carouselRef}
      >
        <div className="text-center text-white/50">No featured content available</div>
      </div>
    );
  }

  const currentChannel = channels[currentIndex];

  // Get wallpaper image or fallback to other available images
  const backgroundImage =
    currentChannel.wallpaper && currentChannel.wallpaper !== ''
      ? currentChannel.wallpaper
      : currentChannel.image &&
        currentChannel.image.wallpaper &&
        currentChannel.image.wallpaper !== ''
      ? currentChannel.image.wallpaper
      : currentChannel.spotlight_poster ||
        currentChannel.poster ||
        (currentChannel.image &&
          (currentChannel.image.spotlight_poster || currentChannel.image.poster)) ||
        'https://via.placeholder.com/1200x600?text=No+Image';

  const title = currentChannel.title || currentChannel.name || 'Untitled Channel';

  // Extract year and duration if available
  const year = currentChannel.year || '';
  const duration = currentChannel.duration || '';

  // Extract genres or categories - ensure it's an array
  const genres = Array.isArray(currentChannel.genres) ? currentChannel.genres : [];
  const genreText = genres.join(', ');

  // Remove the debug placeholder and restore the real hero content
  return (
    <div className="featured-hero-carousel relative w-full h-[100vh]" ref={carouselRef}>
      {channels.length > 0 ? (
        <Slider {...sliderSettings}>
          {channels.map((channel, index) => {
            // Get wallpaper image or fallback to other available images for each slide
            const slideBackgroundImage =
              channel.wallpaper && channel.wallpaper !== ''
                ? channel.wallpaper
                : channel.image && channel.image.wallpaper && channel.image.wallpaper !== ''
                ? channel.image.wallpaper
                : channel.spotlight_poster ||
                  channel.poster ||
                  (channel.image && (channel.image.spotlight_poster || channel.image.poster)) ||
                  'https://via.placeholder.com/1200x600?text=No+Image';

            const slideTitle = channel.title || channel.name || 'Untitled Channel';

            // Extract metadata for badge display - check multiple possible field names
            const slideYear =
              channel.year ||
              channel.release_year ||
              channel.production_year ||
              (channel.video && channel.video.year) ||
              '';

            // Duration could be in seconds (need to format) or already formatted
            let slideDuration = '';
            if (channel.duration) {
              // If it's a number (seconds), convert to readable format
              if (typeof channel.duration === 'number') {
                const hours = Math.floor(channel.duration / 3600);
                const minutes = Math.floor((channel.duration % 3600) / 60);
                if (hours > 0) {
                  slideDuration = `${hours}h ${minutes}m`;
                } else {
                  slideDuration = `${minutes}m`;
                }
              } else {
                slideDuration = channel.duration; // Already formatted
              }
            } else if (channel.runtime) {
              slideDuration = channel.runtime;
            } else if (channel.length) {
              slideDuration = channel.length;
            } else if (channel.video && channel.video.duration) {
              // Video duration might be in seconds
              const videoDuration = channel.video.duration;
              if (typeof videoDuration === 'number') {
                const hours = Math.floor(videoDuration / 3600);
                const minutes = Math.floor((videoDuration % 3600) / 60);
                if (hours > 0) {
                  slideDuration = `${hours}h ${minutes}m`;
                } else {
                  slideDuration = `${minutes}m`;
                }
              } else {
                slideDuration = videoDuration;
              }
            }

            const slideGenres = Array.isArray(channel.genres) ? channel.genres : [];
            const slideGenreText = slideGenres.join(', ');
            const slideRating = channel.rating || '';
            const slideLanguage = channel.languageCode?.toUpperCase() || channel.language || '';
            const slideCountry = channel.country || '';
            const slideProgramType = channel.programming_type || '';

            // Format Tubi-style metadata: "2008 · 1 hr 31 min · Action, Thriller, Adventure"
            const metadataParts = [];
            if (slideYear) {metadataParts.push(slideYear);}
            if (slideDuration) {metadataParts.push(slideDuration);}
            if (slideGenreText) {metadataParts.push(slideGenreText);}
            const formattedMetadata = metadataParts.join(' · ');

            // Check for channel logo in multiple possible locations
            const channelLogo =
              (channel.image && channel.image.channel_logo) ||
              channel.channel_logo ||
              (channel.image && channel.image.logo) ||
              channel.logo ||
              null;

            return (
              <div
                key={`${channel._id || index}-${channelLogo ? 'logo' : 'title'}`}
                className="relative h-[100vh]"
              >
                {/* Background Image with Gradient Overlay */}
                <div
                  className="absolute inset-0 bg-center bg-cover"
                  style={{
                    // Full-screen hero: request a hero-sized image (1920x1080, doubled
                    // to 3840x2160 on high-DPI) so it isn't upscaled from the CDN's
                    // default 1920 width and blurry on retina displays.
                    backgroundImage: `url(${optimizeImageUrl(
                      slideBackgroundImage,
                      getResponsiveImagePreset('CHANNEL_HERO'),
                    )})`,
                  }}
                >
                  {/* Gradient overlay for better text visibility */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  {/* Bottom gradient to blend with page background */}
                  <div
                    className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none"
                    style={{
                      background:
                        'linear-gradient(to top, #0D2638 0%, rgba(13,38,56,0.92) 30%, rgba(13,38,56,0.7) 55%, rgba(13,38,56,0.3) 80%, transparent 100%)',
                    }}
                  ></div>
                </div>

                {/* Overlay Content - Left-aligned to match content sections */}
                <div className="absolute inset-0 flex flex-col justify-center items-start container mx-auto px-6 z-10">
                  {/* Show channel logo if available, otherwise show title */}
                  {channelLogo ? (
                    <img
                      src={channelLogo}
                      alt={slideTitle}
                      className="featured-carousel-logo mb-2 h-auto max-w-md object-contain"
                    />
                  ) : (
                    <h1 className="mb-2 text-4xl font-bold text-white md:text-6xl">{slideTitle}</h1>
                  )}

                  {/* Metadata badges */}
                  <div className="mb-4 flex flex-wrap gap-2">
                    {slideYear && (
                      <span className="px-3 py-1 text-xs font-medium text-white bg-black/60 rounded-md border border-white/20">
                        {slideYear}
                      </span>
                    )}
                    {slideRating && (
                      <span className="px-3 py-1 text-xs font-medium text-white bg-black/60 rounded-md border border-white/20">
                        {slideRating}
                      </span>
                    )}
                    {slideLanguage && (
                      <span className="px-3 py-1 text-xs font-medium text-white bg-black/60 rounded-md border border-white/20">
                        {slideLanguage}
                      </span>
                    )}
                    {slideCountry && (
                      <span className="px-3 py-1 text-xs font-medium text-white bg-black/60 rounded-md border border-white/20">
                        {slideCountry}
                      </span>
                    )}

                    {slideDuration && (
                      <span className="px-3 py-1 text-xs font-medium text-white bg-black/60 rounded-md border border-white/20">
                        {slideDuration}
                      </span>
                    )}
                    {slideProgramType && (
                      <span className="px-3 py-1 text-xs font-medium text-white bg-black/60 rounded-md border border-white/20">
                        {slideProgramType}
                      </span>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4 mb-6">
                    <button
                      type="button"
                      className={`featured-hero-cta flex items-center px-6 py-3 font-bold text-white rounded-md ${
                        channel.subscription_access?.unlocked === false
                          ? 'bg-yellow-500 hover:bg-yellow-600'
                          : 'bg-[#FCC834] hover:bg-[#E0A800]'
                      }`}
                      onClick={() => {
                        setCurrentIndex(index);
                        handleWatchNowClick();
                      }}
                    >
                      {channel.subscription_access?.unlocked === false ? (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            className="w-5 h-5 mr-2"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Buy Now
                        </>
                      ) : (
                        // All Sports.TV titles are free — no need to say "FREE".
                        'Watch Now'
                      )}
                    </button>
                    <button
                      type="button"
                      className="featured-hero-cta flex items-center px-6 py-3 font-bold text-white bg-white/20 rounded-md hover:bg-white/30 border border-white/30"
                      onClick={() => {
                        setCurrentIndex(index);
                        handleLearnMoreClick(channel);
                      }}
                    >
                      Learn More
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </Slider>
      ) : null}

      {/* PDP Modal */}
      {showPDPModal && selectedChannel && (
        <PDPModal
          channel={selectedChannel}
          isVisible={showPDPModal}
          onClose={handleClosePDPModal}
        />
      )}
    </div>
  );
};

export default FeaturedHeroCarousel;
